'use client'
import { MouseEvent } from 'react'
import '../../assets/css/footer.css'
import {
  FooterFacebookIcon,
  FooterInstagramIcon,
  FooterLinkedInIcon,
  FooterTwitterIcon,
  FooterYoutubeIcon,
} from '@/helper/SVG'
import Link from 'next/link'
import NABLLogo from '../../assets/images/nablLogo.png'
import CAPLogo from '../../assets/images/capLogo.png'
import AppleAppStoreImage from '@/assets/images/apple_store.png'
import GooglePlayStoreImage from '@/assets/images/google_play.png'
import Image from 'next/image'
import { FOOTER_DATA } from '@/helper/DATA'
import { isEmpty } from '@/utils/commonFunc'
import { dispatch, useSelector } from '@/lib/store'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import { uploadPrescriptionData } from '@/lib/reducers/patientReducer'
import {
  COMING_SOON_PAGE,
  DISCLAIMER_PAGE,
  DEFAULT_CITY_SLUG,
  EDOS_PAGE,
  FORM_TYPE_LAB_TEST,
  PRIVACY_POLICY_PAGE,
  REFUND_AND_CANCELLATION_PAGE,
  TERMS_AND_CONDITIONS_PAGE,
  SUBSIDIARY_CONFIG,
} from '@/utils/constants'

const LEGAL_LINKS: { title: string; label: string; href: string }[] = [
  {
    title: 'Privacy Policy',
    label: 'Privacy Policy',
    href: PRIVACY_POLICY_PAGE,
  },
  {
    title: 'Disclaimer',
    label: 'Disclaimer',
    href: DISCLAIMER_PAGE,
  },
  {
    title: 'Terms & Condition',
    label: 'Terms & Conditions',
    href: TERMS_AND_CONDITIONS_PAGE,
  },
  {
    title: 'Refund/Cancellation Policy',
    label: 'Refund/Cancellation Policy',
    href: REFUND_AND_CANCELLATION_PAGE,
  },
]

const Footer = () => {
  const commonState = useSelector((state) => state.common) || {}
  const loginState = useSelector((state) => state.login) || {}
  const cityState = useSelector((state) => state.city) || {}
  const { PLAY_STORE_URL, APP_STORE_URL } = SUBSIDIARY_CONFIG

  // useSelector (not loadState) re-renders on city change; no isHydrated needed since this only feeds an href, not text.
  const edosCitySlug = cityState?.currentCityObj?.slugName || DEFAULT_CITY_SLUG
  const edosHref = `${EDOS_PAGE}/${edosCitySlug}`

  const linkOnClick = (
    tagName: string,
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    switch (tagName) {
      case 'Upload Prescription':
        // The link navigates home, where the drawer is mounted - a logged-out user must not be sent there.
        if (!loginState?.isUserLoggedIn) {
          event.preventDefault()
          return dispatch(
            updateLoginData({ ...loginState, loginModalIsOpen: true })
          )
        }
        return dispatch(
          uploadPrescriptionData({ uploadPrescriptionModalIsOpen: true })
        )

      case 'Request a callback':
        return dispatch(
          updateCommonReducerData({
            ...commonState,
            requestCallbackModalIsOpen: {
              modalIsOpen: true,
              type: FORM_TYPE_LAB_TEST,
            },
          })
        )

      default:
        return ''
    }
  }

  const handleGetFooterIcon = (tagName: string) => {
    switch (tagName) {
      case 'FooterYoutubeIcon':
        return <FooterYoutubeIcon />
      case 'FooterInstagramIcon':
        return <FooterInstagramIcon />
      case 'FooterFacebookIcon':
        return <FooterFacebookIcon />
      case 'FooterLinkedInIcon':
        return <FooterLinkedInIcon />
      case 'FooterTwitterIcon':
        return <FooterTwitterIcon />
      default:
        return ''
    }
  }
  return (
    <footer className="bg-gradient-to-r from-primary-100 to-secondary-100 pt-3">
      <div className="bg-white pt-10">
        <div className="container max-w-full ">
          <div className="footer_nav_link">
            {FOOTER_DATA &&
              FOOTER_DATA.length > 0 &&
              FOOTER_DATA.map((item) => {
                // Special rendering for Download the Agilus app column
                if (item.name === 'Download the DDRC Agilus app') {
                  return (
                    <div key={item.name}>
                      <h5 className="text-label-lg font-semibold mb-2">
                        {item.name}
                      </h5>
                      <div className="flex flex-row md:flex-col gap-2">
                        <Link href={APP_STORE_URL} target="_blank">
                          <div className="max-w-36 max-h-11 border border-gray-200 shadow-elevation2 rounded-lg overflow-hidden hover:shadow-elevation3 transition-shadow">
                            <Image
                              src={AppleAppStoreImage}
                              alt="Download Agilus Diagnostics app on App Store"
                              className="w-full h-full"
                            />
                          </div>
                        </Link>
                        <Link href={PLAY_STORE_URL} target="_blank">
                          <div className="max-w-36 max-h-11 border border-gray-200 shadow-elevation2 rounded-lg overflow-hidden hover:shadow-elevation3 transition-shadow">
                            <Image
                              src={GooglePlayStoreImage}
                              alt="Download Agilus Diagnostics app on Google Play"
                              className="w-full h-full"
                            />
                          </div>
                        </Link>
                      </div>
                    </div>
                  )
                }

                return (
                  <div key={item.name}>
                    {item.name && (
                      <h5 className="text-label-lg font-semibold mb-2">
                        {item.name}
                      </h5>
                    )}
                    <ul
                      className={`${
                        item.linksWithImage ? 'flex gap-3 flex-wrap' : ''
                      }`}
                    >
                      {item.content &&
                        item.content.length > 0 &&
                        item.content.map((key) => {
                          return (
                            <li key={key.name}>
                              <Link
                                title={key.name}
                                href={
                                  key?.link === EDOS_PAGE
                                    ? edosHref
                                    : !isEmpty(key?.link)
                                    ? key.link
                                    : COMING_SOON_PAGE
                                }
                                onClick={(event) =>
                                  linkOnClick(key.name, event)
                                }
                                target={key?.external ? '_blank' : '_self'}
                                className="text-label-md-medium leading-6 text-gray-700"
                              >
                                {item.linksWithImage
                                  ? handleGetFooterIcon(key.name)
                                  : key.name}
                              </Link>
                              {!!key?.description ? (
                                <p className="text-body-sm leading-6 text-gray-800">
                                  {key?.description}
                                </p>
                              ) : (
                                ''
                              )}
                            </li>
                          )
                        })}
                    </ul>
                  </div>
                )
              })}
          </div>
        </div>
        {/* Combined footer bottom: logos + copyright + legal links */}
        <div className="border-t border-solid border-gray-200 mt-6 pb-10">
          <div className="container max-w-full py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Accreditation logos */}
              <div className="flex gap-6 items-center">
                <div className="footer_link_image_wrapper">
                  <Image src={NABLLogo} alt="NABL Logo" />
                </div>
                {/* <div className="footer_link_image_wrapper">
                  <Image src={CAPLogo} alt="CAP Logo" />
                </div> */}
              </div>
              {/* Copyright + Legal links */}
              <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
                <p className="text-body-sm text-gray-700">
                  © 2025 DDRC Agilus Diagnostics | All Rights Reserved.
                </p>
                <div className="flex items-center gap-4 flex-wrap justify-center">
                  {LEGAL_LINKS.map(({ title, label, href }) => (
                    <Link
                      key={href}
                      title={title}
                      className="text-label-md-medium text-gray-800 hover:underline"
                      href={href}
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
